import { AuthService, Customer } from '../auth/auth.service';
import { ThemeService } from '../shared/services/theme.service';

import { IsProductActivePipe } from '../pipes/is-product-active.pipe';
import { ErpPermissionPipe } from './pipes/erp-permission.pipe';
import { ShowArchivePipe } from './pipes/show-archive.pipe';
import { VerificationPermissionPipe } from './pipes/verification-permission.pipe';

import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
} from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterModule } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';

import packageInfo from '../../../package.json';
import { Observable } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

export const MODULES = [
  CommonModule,
  MatToolbarModule,
  MatIconModule,
  MatButtonModule,
  MatMenuModule,
  RouterModule,
  MatCardModule,
  ReactiveFormsModule,
  FormsModule,
  MatFormFieldModule,
  MatInputModule,
  MatSidenavModule,
  MatAutocompleteModule,
  MatListModule,
  MatDividerModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [
  IsProductActivePipe,
  ErpPermissionPipe,
  ShowArchivePipe,
  VerificationPermissionPipe,
];

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss', './print-navbar.component.scss'],
  imports: [...MODULES, ...COMPONENTS, ...PIPES],
})
export class NavbarComponent
  implements OnInit, AfterViewInit, AfterViewChecked
{
  private breakpointObserver = inject(BreakpointObserver);
  private cd = inject(ChangeDetectorRef);
  public translate = inject(TranslateService);
  public auth = inject(AuthService);
  private router = inject(Router);
  private themeService = inject(ThemeService);

  isDarkMode = this.themeService.isDarkMode;

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe('(max-width: 800px)')
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  language = signal<string>(undefined);
  userName = signal<string>(undefined);
  userAlias = signal<string>(undefined);
  userIcon = signal<IconName>('user');
  isMaster = signal(false);
  selectedCustomer: any;
  customerList = [];
  showCustomerList = signal(false);
  customerCtrl = new UntypedFormControl();
  filteredCustomers: Observable<Customer[]>;
  showProduct = {
    warning: false,
    eop: false,
    egp: false,
  };
  copyrightYear = signal<number>(undefined);
  appVersion = packageInfo.version;

  hasUserM6Permission = signal(false);
  hasUserArchive = signal(true);
  hasUserLogbook = signal(false);

  constructor() {
    this.language.set(localStorage.getItem('language') || 'de');
    this.filteredCustomers = this.customerCtrl.valueChanges.pipe(
      startWith(''),
      map(customer =>
        customer ? this.filterCustomers(customer) : this.customerList.slice()
      )
    );
  }

  ngOnInit(): void {
    this.copyrightYear.set(new Date().getFullYear());
    this.initUserSettings();
    this.setShowProducts();
    this.checkM6Permission();
    this.checkLogbookPermissions();
    this.themeService.theme();
    /*const savedTheme = localStorage.getItem('theme') || 'light';
    this.isDarkMode = savedTheme === 'dark';
    document.documentElement.setAttribute('data-theme', savedTheme);*/
  }

  ngAfterViewInit(): void {
    this.cd.detectChanges();
  }

  ngAfterViewChecked(): void {
    this.language.set(this.auth.getLanguage());
    this.cd.detectChanges();
  }

  initUserSettings(): void {
    this.language.set(this.auth.getLanguage());

    if (this.auth.authenticated()) {
      const userCustomerDocument = JSON.parse(
        localStorage.getItem('user_customer_document')
      );
      if (userCustomerDocument !== null) {
        this.userName.set(userCustomerDocument['name']);
        this.isMaster.set(userCustomerDocument['isMaster']);
        if (this.isMaster()) {
          if (this.auth.getCustomerAlias()) {
            this.selectedCustomer = this.auth.getCustomerAlias();
            this.userAlias.set(this.selectedCustomer.customerName);
            this.userIcon.set('user-secret');
          }
          this.auth
            .getCustomerList()
            .then(customerList => {
              this.customerList = customerList;
            })
            .catch(() => {
              this.customerList = [];
            });
        }
      }
    }
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  private setShowProducts(): void {
    this.showProduct.warning =
      this.auth.isProductActive('egwProducts') ||
      this.auth.isProductActive('egwSummerProducts');
  }

  private filterCustomers(value: string): Customer[] {
    const filterValue = value.toLowerCase();
    return this.customerList.filter(
      customer => customer.customerName.toLowerCase().indexOf(filterValue) > -1
    );
  }

  onCustomerSelect(customer): void {
    this.selectedCustomer = customer;
    this.auth.beAnotherCustomer(customer).then(() => {
      this.userAlias.set(customer.customerName);
      this.userIcon.set('user-secret');
      this.router.navigate(['/dashboard']).then(() => window.location.reload());
    });
  }

  onCustomerUnselect(): void {
    this.selectedCustomer = null;
    this.userAlias.set(null);
    this.userIcon.set('user');
    this.auth.becomeYourself();
    this.router.navigate(['/dashboard']).then(() => window.location.reload());
  }

  changeLanguage(language): void {
    localStorage.setItem('language', language);
    this.language.set(language);
    const userProfile = this.auth.getUserProfile();
    if (
      !userProfile['metadata'].hasOwnProperty('global') ||
      !userProfile['metadata']['global']
    ) {
      userProfile['metadata']['global'] = {};
    }
    userProfile['metadata']['global']['language'] = language;
    this.auth.updateUserData('global', userProfile['metadata']['global']);
    this.auth.setLanguage();
  }

  onMenuSelected(navbar: MatSidenav): void {
    if (navbar.mode === 'over') {
      navbar.toggle();
    }
  }

  checkM6Permission(): void {
    const customerDocument = this.auth.getCustomerDocument();
    if (customerDocument?.customerId === 10633) {
      this.hasUserM6Permission.set(true);
      this.hasUserArchive.set(false);
    } else if (customerDocument['isMaster']) {
      this.hasUserM6Permission.set(true);
    }
  }

  async checkLogbookPermissions(): Promise<void> {
    this.auth.hasCustomerLogbook().then((hasLogbook: boolean) => {
      this.hasUserLogbook.set(hasLogbook);
    });
  }
}
